import cn from "classnames";
import Markdown from "react-markdown";
import { Link } from "react-router";
import CheckIcon from "@icons/check-circle.svg?react";
import XCircleIcon from "@icons/x-circle.svg?react";
import MinusCircleIcon from "@icons/minus-circle.svg?react";
import RefreshIcon from "@icons/refresh-cw-03.svg?react";
import CornerDownLeftIcon from "@icons/corner-down-left.svg?react";
import { ChatMessagePossiblyOptimistic } from "@/common/utils/types";
import { useThinkingMessage } from "@/common/hooks/useThinkingMessage";
import { useMemo } from "react";
import { normalizeScoutChatMarkdown } from "@/common/utils/normalizeScoutChatMarkdown";

const formatTimestamp = (timestamp: Date) => {
    // Defensive check: ensure timestamp is a Date object
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (messageDate.getTime() === today.getTime()) {
        // Today - show only time
        return date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
    } else {
        // Not today - show date and time
        return date.toLocaleString([], {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    }
};

/**
 * Checks if a string is a valid UUID.
 */
const isUuid = (text: string) => {
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(text);
};

interface ChatMessageProps {
    message: ChatMessagePossiblyOptimistic;
    handleAccept?: (message: ChatMessagePossiblyOptimistic) => void;
    handleReject?: (message: ChatMessagePossiblyOptimistic) => void;
    handleRetry?: (messageId: string) => void;
    handleAgain?: (messageId: string) => void;
    disableSend?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
    message,
    handleAccept = () => {},
    handleReject = () => {},
    handleRetry = () => {},
    handleAgain = () => {},
    disableSend = true,
}) => {
    const workingInitialMessages = useMemo(
        () => [message.actionStatus === "retrying" ? "Retrying..." : "Working...", "Working hard..."],
        [message.actionStatus],
    );

    const { message: workingText } = useThinkingMessage({
        initialMessages: workingInitialMessages,
        enabled: message.actionStatus === "working" || message.actionStatus === "retrying",
    });

    const markdownText = useMemo(() => normalizeScoutChatMarkdown(message.text), [message.text]);

    const markdownComponents = useMemo(
        () => ({
            a: ({
                node: _node,
                href,
                children,
                ...anchorProps
            }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) => {
                if (href?.startsWith("/")) {
                    return (
                        <Link to={href} {...anchorProps}>
                            {children}
                        </Link>
                    );
                }
                return (
                    <a href={href} target="_blank" rel="noopener noreferrer" {...anchorProps}>
                        {children}
                    </a>
                );
            },
        }),
        [],
    );

    // Message ID is not a UUID while the message is an optimistic update, so we disable actions until the message is synced from the server
    const disableActions = disableSend || !isUuid(message.id);
    return (
        <div className={cn("flex", message.isUser ? "justify-end" : "justify-start")}>
            {message.isUser ? (
                <div className="scout-message-user max-w-xs px-4 py-2 rounded-lg bg-max-900 text-white group">
                    <div className="text-sm whitespace-pre-wrap">
                        <Markdown components={markdownComponents}>{markdownText}</Markdown>
                    </div>
                    <div className="relative mt-1.5">
                        {message.timestamp && (
                            <p className="text-xs text-max-200">{formatTimestamp(message.timestamp)}</p>
                        )}
                        {message.actionIntent !== "drop-file" && (
                            <div className="opacity-0 group-hover:opacity-100 absolute bottom-0 right-0 transition-opacity">
                                <button
                                    disabled={disableSend}
                                    onClick={() => handleAgain(message.id)}
                                    className="secondary-button button-xs"
                                >
                                    <CornerDownLeftIcon className="h-3 w-3" />
                                    Again
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="scout-message-ai w-full">
                    <div className="flex">
                        <div className="w-1 bg-gray-300 mr-3"></div>
                        <div className="flex-1">
                            <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                <Markdown components={markdownComponents}>{markdownText}</Markdown>
                            </div>
                            {message.timestamp && (
                                <p className="text-xs mt-1 text-gray-500">{formatTimestamp(message.timestamp)}</p>
                            )}
                        </div>
                    </div>

                    {/* Action buttons for AI messages */}
                    {message.actionStatus === "pending" && (
                        <div className="flex gap-2 mt-3 ml-4">
                            <button
                                disabled={disableActions}
                                onClick={() => handleAccept(message)}
                                className="primary-button button-sm"
                            >
                                <CheckIcon className="h-4 w-4" />
                                Accept
                            </button>
                            <button
                                disabled={disableActions}
                                onClick={() => handleReject(message)}
                                className="secondary-button button-sm"
                            >
                                <XCircleIcon className="h-4 w-4" />
                                Reject
                            </button>
                            {message.actionIntent !== "create-document-template-from-pdf" && (
                                <button
                                    disabled={disableActions}
                                    onClick={() => handleRetry(message.id)}
                                    className="secondary-button button-sm"
                                >
                                    <RefreshIcon className="h-4 w-4" />
                                    Retry
                                </button>
                            )}
                        </div>
                    )}

                    {/* Status indicators */}
                    {message.actionStatus === "accepted" && (
                        <div className="flex items-center gap-1 mt-2 ml-4 text-xs text-max-900">
                            <CheckIcon className="h-3 w-3" />
                            Accepted
                        </div>
                    )}
                    {message.actionStatus === "rejected" && (
                        <div className="flex items-center gap-1 mt-2 ml-4 text-xs text-red-600">
                            <XCircleIcon className="h-3 w-3" />
                            Rejected
                        </div>
                    )}
                    {message.actionStatus === "cancelled" && (
                        <div className="flex items-center gap-1 mt-2 ml-4 text-xs text-gray-400">
                            <MinusCircleIcon className="h-3 w-3" />
                            Cancelled
                        </div>
                    )}
                    {message.actionStatus === "failed" && (
                        <div className="flex items-center gap-1 mt-2 ml-4 text-xs text-red-600">
                            <XCircleIcon className="h-3 w-3" />
                            Failed
                        </div>
                    )}
                    {(message.actionStatus === "working" || message.actionStatus === "retrying") && (
                        <div className="flex items-center gap-1 mt-2 ml-4 text-xs text-gray-600">
                            <RefreshIcon className="h-3 w-3 animate-spin" />
                            {workingText}
                        </div>
                    )}
                    {message.actionStatus === "completed" && (
                        <div className="flex items-center gap-1 mt-2 ml-4 text-xs text-gray-600">
                            <CheckIcon className="h-3 w-3" />
                            Done
                        </div>
                    )}
                    {message.retriedAt && (
                        <div className="flex items-center gap-1 mt-2 ml-4 text-xs text-max-900">
                            <CheckIcon className="h-3 w-3" />
                            Retried at {formatTimestamp(message.retriedAt)}
                        </div>
                    )}
                    {message.retryFailedAt && (
                        <div className="flex items-center gap-1 mt-2 ml-4 text-xs text-red-600">
                            <XCircleIcon className="h-3 w-3" />
                            Retry failed at {formatTimestamp(message.retryFailedAt)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ChatMessage;
